import {
  currencyFormatter,
  currencySymbol,
  IPortalInvoiceResponse,
  REQUIRES_VERIFY_MESSAGE,
  toDecimal,
} from "shared";
import { useState } from "react";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import { CalendarDaysIcon, ClockIcon } from "@heroicons/react/24/outline";

import { Button } from "../../../common/Atoms/Button";
import { Modal } from "../../../common/Atoms/Modal";
import { DatePicker } from "../../../common/Atoms/DatePicker";
import { CurrencyInput } from "../../../common/Atoms/CurrencyInput";
import { useCreatePaymentPromiseMutation } from "../../services/api/invoice";
import { useSession } from "../../hooks/useSession";

import { InvoiceOption } from "./InvoiceOption";

interface IPromiseToPayProps {
  invoiceData: IPortalInvoiceResponse;
  onRequireVerification: () => void;
}

export function canPromiseToPay(invoiceData: IPortalInvoiceResponse) {
  return !!invoiceData.promisesEnabled && invoiceData.invoice?.xero_status === `AUTHORISED` && (invoiceData.invoice.xero_amount_due ?? 0) > 0;
}

export function PromiseToPay({ invoiceData, onRequireVerification }: IPromiseToPayProps) {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const [promisedDate, setPromisedDate] = useState<string>(``);
  const amountDue = invoiceData.invoice?.xero_amount_due ?? 0;
  const [amountCents, setAmountCents] = useState<number>(amountDue);
  const [createPromise, { isLoading }] = useCreatePaymentPromiseMutation();

  if (!canPromiseToPay(invoiceData)) {
    return null;
  }

  const format = currencyFormatter(invoiceData.invoice.xero_currency);
  const current = invoiceData.currentPromise;
  const amountInvalid = !Number.isInteger(amountCents) || amountCents < 1 || amountCents > amountDue;

  async function onSubmit() {
    if (!session?.id) return;

    try {
      await createPromise({
        code: invoiceData.invoice.hash,
        sessionId: session.id,
        promisedDate,
        promisedAmountCents: amountCents,
      }).unwrap();

      toast.success(`Thanks, your payment promise has been recorded`);
      setOpen(false);
    }
    catch (err) {
      const status = err?.status;
      const message = err?.data?.message || err?.data;

      if (status === 401 || message === REQUIRES_VERIFY_MESSAGE) {
        setOpen(false);
        onRequireVerification();

        return;
      }

      toast.error(typeof message === `string` ? message : `Could not record your payment promise`);
    }
  }

  if (current) {
    return (
      <InvoiceOption
        icon={ ClockIcon }
        tone={ `blue` }
        title={ `Payment promised` }
        description={ `You have promised to pay ${format(toDecimal(current.promisedAmountCents))} by ${DateTime.fromISO(current.promisedDate).toFormat(`ccc dd LLL yyyy`)}.` }
      />
    );
  }

  return (
    <>
      <InvoiceOption
        icon={ CalendarDaysIcon }
        title={ `Promise to pay` }
        description={ `Can't pay today? Let us know when you will pay this invoice.` }
        onClick={ () => setOpen(true) }
      />

      <Modal
        open={ open }
        setOpen={ setOpen }
        title={ `Promise to pay` }
      >
        <div className={ `space-y-3` }>
          <DatePicker
            label={ `I will pay by` }
            name={ `promisedDate` }
            value={ promisedDate }
            onChange={ ({ value }) => setPromisedDate(value || ``) }
            disableClear
          />
          <CurrencyInput
            label={ `Amount` }
            name={ `promisedAmountCents` }
            prefix={ currencySymbol(invoiceData.invoice.xero_currency) }
            value={ amountCents }
            onChange={ ({ value }) => setAmountCents(Number(value)) }
            description={ amountInvalid ? `Must be between 0.01 and the amount due (${format(toDecimal(amountDue))})` : `Amount due is ${format(toDecimal(amountDue))}` }
          />
          <div className={ `flex justify-end gap-2` }>
            <Button color={ `none` }
              onClick={ () => setOpen(false) }
              disabled={ isLoading }>
              { `Cancel` }
            </Button>
            <Button onClick={ onSubmit }
              loading={ isLoading }
              disabled={ !promisedDate || amountInvalid || isLoading }>
              { `Submit promise` }
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
